import { gql } from "@apollo/client";
import { IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import FormFieldContainer from "controls/FormFieldContainer";
import useData from "hooks/useData";
import useFormDialogs from "hooks/useFormDialogs";
import { Delete, Information, Plus } from "mdi-material-ui";
import React, { useCallback, useEffect, useId, useMemo, useState } from "react";

import SelectReadminObjectDialog from "./SelectReadminObjectDialog";
import ReadminObjectViewDetailsDialog from "./ViewDetailsDialog";

const makeReadminObjectKey = (readminObject) =>
  readminObject ? `${readminObject.__typename}:${readminObject.id}` : "";

ReadminObjectsInput.toFormData = (value) => {
  return value?.map((readminObject) => ({
    id: readminObject.id,
    typename: readminObject.__typename,
  }));
};

ReadminObjectsInput.defaultValue = [];

export default function ReadminObjectsInput({
  formField,
  value,
  onChange,
  disabled,
  optionsTypename,
  optionsFilters = {},
  ...others
}) {
  const id = useId();
  const [formDialogs, formDialogsOpen] = useFormDialogs();
  // Note: Multi-selectable is false by default, but can be overridden by passing optionsMultiSelectable prop
  const { optionsMultiSelectable: isMultiSelectable = false, helperText, ...tableProps } = others;
  const readminObjects = value ?? ReadminObjectsInput.defaultValue;
  const [objectNames, objectNamesSet] = useState(() => new Map());
  const cacheReadminObjectName = useCallback((readminObject, readminName) => {
    if (!readminObject || !readminName) return;
    const key = makeReadminObjectKey(readminObject);
    objectNamesSet((previous) => {
      if (previous.get(key) === readminName) return previous;
      const next = new Map(previous);
      next.set(key, readminName);
      return next;
    });
  }, []);
  const getReadminObjectName = useCallback(
    (readminObject) => {
      if (!readminObject) return "";
      return readminObject.readminName ?? objectNames.get(makeReadminObjectKey(readminObject)) ?? "";
    },
    [objectNames],
  );
  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: "base", numeric: true }), []);
  const sortedReadminObjects = useMemo(() => {
    return [...readminObjects].sort((objectA, objectB) => {
      const nameComparison = collator.compare(getReadminObjectName(objectA), getReadminObjectName(objectB));
      if (nameComparison !== 0) return nameComparison;
      return makeReadminObjectKey(objectA).localeCompare(makeReadminObjectKey(objectB));
    });
  }, [collator, getReadminObjectName, readminObjects]);
  const removeReadminObject = useCallback(
    (target) => {
      const targetIndex = readminObjects.indexOf(target);
      if (targetIndex === -1) return;
      onChange([...readminObjects.slice(0, targetIndex), ...readminObjects.slice(targetIndex + 1)]);
    },
    [onChange, readminObjects],
  );

  return (
    <FormFieldContainer data-readmin-objects-input id={id} disabled={disabled} helperText={helperText} {...tableProps}>
      {formDialogs}
      <List disablePadding>
        {!readminObjects.length && disabled && (
          <ListItem>
            <ListItemText primary="(empty)" />
          </ListItem>
        )}
        {sortedReadminObjects.map((readminObject) => (
          <ListItem
            key={makeReadminObjectKey(readminObject)}
            secondaryAction={
              <>
                <IconButton
                  onClick={() =>
                    formDialogsOpen(
                      <ReadminObjectViewDetailsDialog id={readminObject.id} typename={readminObject.__typename} />,
                    )
                  }
                >
                  <Information />
                </IconButton>
                {!disabled && (
                  <IconButton edge="end" onClick={() => removeReadminObject(readminObject)}>
                    <Delete />
                  </IconButton>
                )}
              </>
            }
          >
            <ObjectListItemText readminObject={readminObject} onNameLoaded={cacheReadminObjectName} />
          </ListItem>
        ))}
        {!disabled && (
          <ListItem disablePadding>
            <ListItemButton
              data-readmin-objects-input-add
              onClick={() =>
                formDialogsOpen(
                  <SelectReadminObjectDialog
                    {...tableProps}
                    selectedReadminObjects={readminObjects}
                    optionsTypename={optionsTypename}
                    optionsFilters={optionsFilters}
                    optionsMultiSelectable={isMultiSelectable}
                    onSubmit={(valueNew) => onChange([...readminObjects, valueNew])}
                    {...(tableProps.label && { header: `${tableProps.label}...` })}
                  />,
                )
              }
            >
              <ListItemIcon>
                <Plus />
              </ListItemIcon>
              <ListItemText primary="Add" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </FormFieldContainer>
  );
}

function ObjectListItemText({ readminObject, onNameLoaded }) {
  const [data] = useData(
    gql`
      query ReadminObjectsInput($id: ID, $typename: String) {
        readminObject(id: $id, typename: $typename) {
          id
          readminName
        }
      }
    `,
    {
      id: readminObject.id,
      typename: readminObject.__typename,
    },
  );

  const resolvedName = data?.readminObject?.readminName ?? readminObject.readminName;
  useEffect(() => {
    if (!onNameLoaded || !readminObject || !resolvedName) return;
    onNameLoaded(readminObject, resolvedName);
  }, [onNameLoaded, readminObject, resolvedName]);

  return <ListItemText primary={resolvedName} />;
}
